import React, {ReactNode} from 'react';
import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import {useDispatch, useSelector} from 'react-redux';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {Accordion, AccordionSummary, Typography, AccordionDetails, Grid, Toolbar} from '@material-ui/core';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import PublishRoundedIcon from '@material-ui/icons/PublishRounded';
import {ModalState, showModal} from 'Core/ducks/modal';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {Scope} from 'Shared/constants/Scope';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {Link} from 'react-router-dom';

type BrokerSpreadsheetFileSummaryAccordionProps = {
  enrollmentFile: EnrollmentApplication;
  enrollmentFileHistory: EnrollmentApplication[];
  fileId: string;
};

const BrokerSpreadsheetFileSummaryAccordion = (props: BrokerSpreadsheetFileSummaryAccordionProps) => {
  const {enrollmentFile, enrollmentFileHistory, fileId} = props;
  const scopes = useSelector(selectUserScopes);
  const dispatch = useDispatch();

  const hasScopeToCancelFileUpload = (scopes: Scope[]) => scopes.some(scope => scope === Scope.CancelFileUpload);

  const hasAbilityToUploadEnrollmentApplications = (scopes: Scope[]) =>
    scopes.some(scope => /et.uploadenrollmentapplicationsfile|\|.+/.test(scope));

  return (
    <>
      <Toolbar className="external-enrollment-file__content-container-header">
        <Typography className="external-enrollment-file__content-container-header-title" variant="h6">
          File Summary
        </Typography>

        {hasAbilityToUploadEnrollmentApplications(scopes) &&
          enrollmentFile.groupId! &&
          enrollmentFile.invalidRecordCount! > 0 && (
            <Tooltip title="Upload File">
              <IconButton
                className="btn-upload"
                onClick={() =>
                  dispatch(
                    showModal(
                      ModalState.EnrollmentApplicationUpload({
                        fileId: fileId!,
                        groupId: enrollmentFile.groupId!,
                      })
                    )
                  )
                }
                aria-label="Upload File"
              >
                <PublishRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
        {hasScopeToCancelFileUpload(scopes) && enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.RECEIVED && (
          <Tooltip title="Cancel File">
            <IconButton
              className="btn-cancel"
              onClick={() =>
                dispatch(
                  showModal(
                    ModalState.BrokerSpreadSheetCancelConfirmation({
                      fileId: fileId!,
                    })
                  )
                )
              }
              aria-label="Cancel File"
            >
              <CancelRoundedIcon />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <div className="summary-line-info">
        <Accordion className="summary-line-info__expansion-panel">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="summary-line-info__expansion-panel__icon-container">
              <i className="icon-container__file-icon"></i>
            </div>
            <div className="summary-line-info__expansion-panel__file-details">
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">File Name:</span>
                <span className="value">{enrollmentFile.fileName}</span>
              </div>

              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Uploaded:</span>
                <span className="value">{toFormattedDateTime(enrollmentFile.fileUploadedDate!)}</span>
              </div>

              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Record Count:</span>
                <span className="value">{enrollmentFile.rowCount}</span>
              </div>
            </div>
          </AccordionSummary>

          <AccordionDetails className="summary-line-info__expansion-panel-details">
            <div className="summary-line-info__expansion-panel-details__actions-container">
              <Typography className="summary-line-info__expansion-panel-details__actions-container--title" variant="h6">
                File History Information
              </Typography>
            </div>
            {pipe(
              enrollmentFileHistory,
              NEA.fromArray,
              O.map(enrollmentFiles => (
                <>
                  {enrollmentFiles.map(fileProfile => (
                    <div className="summary-line-info__expansion-panel-details__history-list" key={fileProfile.id!}>
                      <Grid container spacing={4}>
                        <Grid item xs={12} sm={6} md={3}>
                          <span className="title">File Name: </span>
                          <span className="value">{fileProfile.fileName}</span>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <span className="title">Uploaded: </span>
                          <span className="value">{toFormattedDateTime(fileProfile.fileUploadedDate!)}</span>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <span className="title">Record Count: </span>
                          <span className="value">{fileProfile.rowCount}</span>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <span className="title">Error Count: </span>
                          <span className="value">
                            {' '}
                            {fileProfile.invalidRecordCount !== 0 ? (
                              <Link
                                className="error-link"
                                to={EnrollmentRouteConstants.BROKER_SPREADSHEET_STATUS_ERROR_REVIEW(fileProfile.id!)}
                              >
                                {fileProfile.invalidRecordCount}
                              </Link>
                            ) : (
                              fileProfile.invalidRecordCount
                            )}
                          </span>
                        </Grid>
                      </Grid>
                    </div>
                  ))}
                </>
              )),
              O.getOrElse(
                (): ReactNode => (
                  <div className="summary-line-info__expansion-panel-details__history-list">
                    No History Information Available
                  </div>
                )
              )
            )}
          </AccordionDetails>
        </Accordion>
      </div>
    </>
  );
};

export default BrokerSpreadsheetFileSummaryAccordion;
